// Subscribe / unsubscribe to web push. Owns the PushManager wiring so
// callers can flip a single state hook from a button.
//
// Flow on enable:
//   1. Wait for the service worker to be ready.
//   2. Ask the browser for Notification permission.
//   3. Fetch the server's VAPID public key.
//   4. PushManager.subscribe({ applicationServerKey, userVisibleOnly: true }).
//   5. POST the subscription JSON to /api/push/subscribe.

import { useCallback, useEffect, useState } from "react";
import { api } from "./api";

function urlBase64ToUint8Array(base64String: string): ArrayBuffer {
  const padding = "=".repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding).replace(/-/g, "+").replace(/_/g, "/");
  const raw = atob(base64);
  const buf = new ArrayBuffer(raw.length);
  const view = new Uint8Array(buf);
  for (let i = 0; i < raw.length; i++) view[i] = raw.charCodeAt(i);
  return buf;
}

type PushState = {
  supported: boolean;
  permission: NotificationPermission | "unsupported";
  subscribed: boolean;
  busy: boolean;
  enable: () => Promise<void>;
  disable: () => Promise<void>;
  test: () => Promise<{ sent: number; pruned: number; failed: number } | null>;
  error: string | null;
};

export function usePushSubscription(): PushState {
  const supported =
    typeof window !== "undefined" &&
    "serviceWorker" in navigator &&
    "PushManager" in window &&
    "Notification" in window;

  const [permission, setPermission] = useState<
    NotificationPermission | "unsupported"
  >(() => (supported ? Notification.permission : "unsupported"));
  const [subscribed, setSubscribed] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!supported) return;
    let cancelled = false;
    (async () => {
      const reg = await navigator.serviceWorker.ready;
      const existing = await reg.pushManager.getSubscription();
      if (!cancelled) setSubscribed(!!existing);
    })().catch(() => {});
    return () => {
      cancelled = true;
    };
  }, [supported]);

  const enable = useCallback(async () => {
    if (!supported) return;
    setBusy(true);
    setError(null);
    try {
      const perm = await Notification.requestPermission();
      setPermission(perm);
      if (perm !== "granted") {
        setError("Permission denied — enable notifications in browser settings.");
        return;
      }
      const reg = await navigator.serviceWorker.ready;
      const { publicKey } = await api<{ publicKey: string | null }>(
        "/push/vapid-key",
        { public: true },
      );
      if (!publicKey) {
        setError("Server VAPID key not configured.");
        return;
      }
      let sub = await reg.pushManager.getSubscription();
      if (!sub) {
        sub = await reg.pushManager.subscribe({
          userVisibleOnly: true,
          applicationServerKey: urlBase64ToUint8Array(publicKey),
        });
      }
      const json = sub.toJSON() as {
        endpoint: string;
        keys?: { p256dh?: string; auth?: string };
      };
      await api("/push/subscribe", {
        method: "POST",
        body: {
          endpoint: json.endpoint,
          keys: {
            p256dh: json.keys?.p256dh ?? "",
            auth: json.keys?.auth ?? "",
          },
          userAgent: navigator.userAgent,
        },
      });
      setSubscribed(true);
    } catch (e) {
      setError((e as Error).message ?? "Could not enable notifications.");
    } finally {
      setBusy(false);
    }
  }, [supported]);

  const disable = useCallback(async () => {
    if (!supported) return;
    setBusy(true);
    setError(null);
    try {
      const reg = await navigator.serviceWorker.ready;
      const sub = await reg.pushManager.getSubscription();
      if (sub) {
        await api("/push/unsubscribe", {
          method: "POST",
          body: { endpoint: sub.endpoint },
        });
        await sub.unsubscribe();
      }
      setSubscribed(false);
    } catch (e) {
      setError((e as Error).message ?? "Could not disable notifications.");
    } finally {
      setBusy(false);
    }
  }, [supported]);

  const test = useCallback(async () => {
    if (!supported || !subscribed) return null;
    setError(null);
    try {
      return await api<{ sent: number; pruned: number; failed: number }>(
        "/push/test",
        { method: "POST" },
      );
    } catch (e) {
      setError((e as Error).message ?? "Test send failed.");
      return null;
    }
  }, [supported, subscribed]);

  return { supported, permission, subscribed, busy, enable, disable, test, error };
}
